import { FileText, Upload, X } from 'lucide-react';
import { useId, useRef, useState, type DragEvent } from 'react';
import { Button } from '../primitives/button';
import { cn } from '../lib/cn';

interface FileUploadProps {
  /** e.g. ['image/jpeg', 'image/png', 'application/pdf'] */
  accept: string[];
  maxSizeBytes: number;
  maxFiles?: number;
  files: File[];
  onFilesChange: (files: File[]) => void;
  /** Called with a readable reason when a file is refused */
  onReject?: (message: string) => void;
  label?: string;
  hint?: string;
  disabled?: boolean;
  className?: string;
  /** Texts for the button and the reasons a file is refused; English by default */
  labels?: Partial<FileUploadLabels>;
}

export interface FileUploadLabels {
  choose: string;
  remove: (name: string) => string;
  typeNotAllowed: (name: string) => string;
  tooLarge: (name: string, limit: string) => string;
  tooMany: (max: number) => string;
}

const defaultLabels: FileUploadLabels = {
  choose: 'Choose files',
  remove: (name) => `Remove ${name}`,
  typeNotAllowed: (name) => `${name}: this file type is not allowed.`,
  tooLarge: (name, limit) => `${name}: larger than ${limit}.`,
  tooMany: (max) => `You can add up to ${max} files.`,
};

export const formatFileSize = (bytes: number) =>
  bytes < 1024 * 1024 ? `${Math.ceil(bytes / 1024)} KB` : `${(bytes / (1024 * 1024)).toFixed(1)} MB`;

/**
 * Drop zone with type, size and count checks in the browser. The server checks again
 * (by file content) — this is only for quick feedback.
 */
export function FileUpload({
  accept,
  maxSizeBytes,
  maxFiles = 5,
  files,
  onFilesChange,
  onReject,
  label = 'Drag files here or choose files',
  hint,
  disabled = false,
  className,
  labels: labelOverrides,
}: FileUploadProps) {
  const text = { ...defaultLabels, ...labelOverrides };
  const inputId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);

  const addFiles = (incoming: FileList | null) => {
    if (!incoming || disabled) return;
    const accepted: File[] = [];
    for (const file of Array.from(incoming)) {
      if (!accept.includes(file.type)) onReject?.(text.typeNotAllowed(file.name));
      else if (file.size > maxSizeBytes) onReject?.(text.tooLarge(file.name, formatFileSize(maxSizeBytes)));
      else accepted.push(file);
    }
    const next = [...files, ...accepted];
    if (next.length > maxFiles) onReject?.(text.tooMany(maxFiles));
    onFilesChange(next.slice(0, maxFiles));
  };

  const onDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setDragging(false);
    addFiles(event.dataTransfer.files);
  };

  return (
    <div className={cn('grid gap-3', className)}>
      <div
        onDragOver={(event) => {
          event.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
        className={cn(
          'flex flex-col items-center gap-2 rounded-xl border-2 border-dashed border-border px-6 py-8 text-center transition-colors',
          dragging && 'border-primary bg-primary/5',
          disabled && 'opacity-50',
        )}
      >
        <Upload className="size-6 text-muted-foreground" />
        <label htmlFor={inputId} className="text-sm font-medium">
          {label}
        </label>
        {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
        <Button variant="outline" size="sm" onClick={() => inputRef.current?.click()} disabled={disabled}>
          {text.choose}
        </Button>
        <input
          ref={inputRef}
          id={inputId}
          type="file"
          multiple={maxFiles > 1}
          accept={accept.join(',')}
          className="sr-only"
          disabled={disabled}
          onChange={(event) => {
            addFiles(event.target.files);
            event.target.value = '';
          }}
        />
      </div>
      {files.length > 0 && (
        <ul className="grid gap-2">
          {files.map((file, index) => (
            <li
              key={`${file.name}-${index}`}
              className="flex items-center gap-3 rounded-lg border border-border bg-surface px-3 py-2 text-sm"
            >
              <FileText className="size-4 shrink-0 text-muted-foreground" />
              <span className="min-w-0 flex-1 truncate">{file.name}</span>
              <span className="text-xs text-muted-foreground tabular-nums">{formatFileSize(file.size)}</span>
              <Button
                variant="ghost"
                size="icon"
                className="size-7"
                aria-label={text.remove(file.name)}
                onClick={() => onFilesChange(files.filter((_, i) => i !== index))}
              >
                <X />
              </Button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
